import type { FormDocument } from '@verifiedit/form-generator'
import { defaultDocumentStatus } from '@/services/api/site'
import { explainDocument } from '@/types/documents'
import { fetchUserPhotoContent } from '@/services/api/user'
import { refusedContentStatus } from '@/services/api/documentContent'
import Log from '@/utilities/Log'
import { useEmployeePhotoStore } from '@/storage/employeePhoto'
import { useUserStore } from '@/storage/user'

/**
 * Fetch the bytes of the logged-in user's employee photo, if the reference login gave us says
 * there are any to fetch, and hold them where the screens that show the photo can find them.
 *
 * Started at login and again on session restore, in the background both times: by the time a user
 * finishes checking in, the photo is already there. Nothing waits on it and nothing fails because
 * of it — a caller that has just authenticated a user is not the place to surface a photo problem
 * — so this never rejects, and a photo it cannot produce is a photo the screen explains (B40).
 */
export default async function loadEmployeePhoto(): Promise<void> {
  const call = ++latestCall
  const { employeePhoto, id } = useUserStore()
  const { setPhoto } = useEmployeePhotoStore()
  const reference = referenceIn(employeePhoto)

  if (reference === null) {
    setPhoto(null)

    return
  }

  const photo: FormDocument = {
    // There is no filename column behind an employee photo, and none is invented for one: the
    // photo is shown, never offered as a download (B54/B56).
    name: null,
    status: reference.status,
    uuid: reference.uuid,
  }

  if (photo.status === 'available') {
    // The reference goes up before the bytes, so a screen that shows the photo while it is still
    // loading reads it as on its way rather than as a photo that does not exist.
    setPhoto({ ...photo })

    try {
      const { content, mimeType } = await fetchUserPhotoContent(id)

      // On a shared site device the user can log out, and the next one log in, while this was
      // in flight: their photo must never land on someone else's check-in screen.
      if (isSuperseded(call, id)) {
        URL.revokeObjectURL(content)

        return
      }

      photo.content = content
      photo.mimeType = mimeType
    } catch (error) {
      if (isSuperseded(call, id)) {
        return
      }

      // A status that says `available` over bytes nobody can produce is two reads of the same
      // columns disagreeing — reported as the fault it is, and shown as a photo with nothing
      // behind it (see the register, B90).
      Log.notify(
        error as Error,
        'Employee photo content contradicted its status',
      )

      photo.status = refusedContentStatus(error)
    }
  }

  setPhoto(explainDocument(photo))
}

let latestCall = 0

function isSuperseded(call: number, userId: number): boolean {
  return call !== latestCall || useUserStore().id !== userId
}

/**
 * The reference is only ever written by a login response, but the store it is written to is
 * persisted: a device that ran a version of this app which held the photo itself rehydrates the
 * base64 string it saved where the reference now goes. That is not a reference, and those bytes
 * are never rendered — the photo is fetched again or explained, never restored from disk.
 */
function referenceIn(
  value: unknown,
): { status: FormDocument['status']; uuid: string } | null {
  if (value === null || typeof value !== 'object') {
    return null
  }

  const { status, uuid } = value as Record<string, unknown>

  if (typeof uuid !== 'string' || uuid === '') {
    return null
  }

  return {
    status: defaultDocumentStatus(status),
    uuid: uuid,
  }
}
